import { ApolloQueryResult } from '@apollo/client';
import {
	T,
	always,
	and,
	anyPass,
	complement,
	cond,
	curry,
	endsWith,
	equals,
	filter,
	find,
	head,
	ifElse,
	includes,
	isNil,
	keys,
	last,
	length,
	map,
	max,
	min,
	not,
	or,
	propEq,
	reduce,
	split,
	type,
	when,
	without,
} from 'ramda';

import { FormData } from '@folx-types/flow-steps';

export type IMedPrice = Record<string, number>;
export type IPriceRange = { min: number; max: number };
export interface IRxCategoryRouteNode extends IRxCategoryRoute {
	rxCategoryMedsByRxCategoryRouteKey: {
		edges: [
			{
				node: IRxCategoryMed;
			}
		];
	};
}

export interface IRxCategoryRouteAndMeds {
	rxCategoryRoutes: {
		edges: [
			{
				node: IRxCategoryRouteNode;
			}
		];
	};
}

export interface IRxCategoryDoses {
	rxCategoryDoses: {
		edges: [
			{
				node: IRxCategoryDose;
			}
		];
	};
}

export interface IMedication {
	key: string;
	label: string;
	description?: string;
}

export interface IMedInfo {
	route: string | null | undefined;
	medication?: string | null;
	type?: string | null;
	frequency?: string | null;
	dose?: string | null;
	custom?: string | null;
}

export const isDefaultMedOption = (key: string) =>
	or(endsWith('.no', key), endsWith('.dont_know', key));

export const isEstablished = (membershipType: 'new_start' | 'established' | '' = '') => {
	return equals('established', membershipType);
};

export const isValidMed = (med: string) =>
	not(or(endsWith('.no', med), endsWith('.dont_know', med)));

export const getNameForRouteKey = (routeKey: string) => {
	const medKey = last(split('.', routeKey)) ?? '';
	return cond([
		[equals('spironolactone'), always('Spironolactone')],
		[equals('finasteride'), always('Finasteride')],
		[equals('dutasteride'), always('Dutasteride')],
		[T, always('')],
	])(medKey);
};

type GetCategoryMeds = (
	flowId: string,
	membershipType?: 'new_start' | 'established'
) => (list: readonly { node: IRxCategoryRouteNode }[]) => string[];
export const getCategoryMeds: GetCategoryMeds = (flowId: string, membershipType) =>
	curry(
		reduce((acc: string[], rxRoute: { node: IRxCategoryRouteNode }) => {
			const meds = reduce(
				(acc: string[], rxCategoryMed) => {
					if (
						!!rxCategoryMed.node.metadata?.disabled_features &&
						!!rxCategoryMed.node.metadata?.disabled_features.length &&
						includes(flowId, rxCategoryMed.node.metadata?.disabled_features)
					) {
						return acc;
					}
					if (equals(rxCategoryMed.node.metadata?.established_only, true)) {
						return ifElse(
							() => isEstablished(membershipType),
							() => {
								return acc.concat(rxCategoryMed.node.rxCategoryMedKey);
							},
							() => {
								return acc;
							}
						)();
					}

					return acc.concat(rxCategoryMed.node.rxCategoryMedKey);
				},
				[],
				rxRoute.node.rxCategoryMedsByRxCategoryRouteKey.edges
			);
			return acc.concat(meds);
		}, [])
	);

export const getMedDoses = (
	results: ApolloQueryResult<IRxCategoryDoses>[],
	doseKeys: string[],
	membershipType?: 'new_start' | 'established'
) =>
	reduce(
		(acc: Record<string, string[]>, result: ApolloQueryResult<IRxCategoryDoses>) => {
			const doseResults = result.data.rxCategoryDoses.edges;
			const doses = reduce(
				(acc: Record<string, string[]>, dose: { node: IRxCategoryDose }) => {
					if (not(includes(dose.node.rxCategoryDoseKey, doseKeys))) {
						return acc;
					}
					if (
						and(
							equals(dose.node.metadata?.established_only, true),
							complement(isEstablished)(membershipType)
						)
					) {
						return acc;
					}

					const accumulator = acc?.[dose.node.rxCategoryMedKey] ?? [];
					const doseAccumulator = {
						[dose.node.rxCategoryMedKey]: accumulator.concat(dose.node.rxCategoryDoseKey),
					};
					return doseAccumulator;
				},
				{},
				doseResults
			);

			return { ...acc, ...doses };
		},
		{},
		results
	);

export const getMedInfoType = (type: string, data: FormData) => {
	const key = find((key) => endsWith(type, key), keys(data));
	return data?.[key];
};

export const getMedInfo = (
	meds: { med_key: string; dose_key: string; route_key?: string }[] = [],
	data: FormData = {}
): IMedInfo[] => {
	return map((med) => {
		const filteredData: FormData = filter((datum) => {
			return equals('String', type(datum)) && includes(med?.route_key ?? med.med_key, datum);
		}, data);

		const medData = {
			route: med?.route_key ?? med.med_key,
			medication: med.med_key,
			type: getMedInfoType('InjectableType', filteredData),
			frequency: getMedInfoType('FrequencyType', filteredData),
			dose: getMedInfoType('DoseType', filteredData),
			custom: getMedInfoType('CustomType', filteredData),
		};

		return medData;
	}, meds);
};

export const getRoutePrices = (flowId: string) =>
	curry(
		reduce((acc: Record<string, IMedPrice>, rxRoute: { node: IRxCategoryRouteNode }) => {
			const meds = reduce(
				(acc: IMedPrice, rxCategoryMed) => {
					if (
						!!rxCategoryMed.node.metadata?.disabled_features &&
						!!rxCategoryMed.node.metadata?.disabled_features.length &&
						includes(flowId, rxCategoryMed.node.metadata?.disabled_features)
					) {
						return acc;
					}
					return {
						...acc,
						[rxCategoryMed.node.rxCategoryMedKey]: rxCategoryMed.node.price ?? NaN,
					};
				},
				{},
				rxRoute.node.rxCategoryMedsByRxCategoryRouteKey.edges
			);

			return { ...acc, [rxRoute.node.rxCategoryRouteKey]: meds };
		}, {})
	);

export const getRoundedPrice = curry((n: number) => Math.round(n / 100));

export const getPriceRange = reduce(
	(acc: IPriceRange, price: number) => {
		if (isNaN(price)) {
			return acc;
		}
		const parsedPrice = getRoundedPrice(price);

		return {
			min: isNaN(acc.min) ? parsedPrice : min(acc.min, parsedPrice),
			max: isNaN(acc.max) ? parsedPrice : max(acc.max, parsedPrice),
		};
	},
	{ min: NaN, max: NaN }
);

export const getRoutePriceRange = (routePrices: number[]) => {
	return not(isNil(routePrices))
		? equals(1, length(routePrices))
			? { min: NaN, max: getRoundedPrice(head(routePrices) ?? NaN) }
			: getPriceRange(routePrices)
		: { min: NaN, max: NaN };
};

export const removeUnavailableMedications = (medList: string[]) => {
	return medList;
};
